import Link from "next/link";
import { PUBLIC_PLANS, type PublicPlan, YEARLY_DISCOUNT } from "../../const/publicPricing";
import type { PricingLimitVariant } from "../../hooks/usePricingLimitVariant";
import { PRICES_UPDATED_ON, type PricingCopy } from "../en/pricing";
import {
  API_PRICE_SUMMARY,
  APP_IN_ENGLISH_FAQ,
  count,
  IS_KLAP_FREE_FAQ,
  isKlapFreeAnswer,
  MAX_HOURS,
  orList,
  usd,
  WATERMARK_ANSWER,
} from "./facts";
import { typo, typoDeep } from "./typo";

const DISCOUNT_PERCENT = Math.round(YEARLY_DISCOUNT * 100);

const UPDATED_ON = new Date(`${PRICES_UPDATED_ON}T00:00:00Z`).toLocaleDateString(
  "fr-FR",
  { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" },
);

const link = "text-violet-600 underline underline-offset-2";

const SUBTITLES: Record<PricingLimitVariant, string> = {
  control: "Ne payez que les clips que vous générez.",
  "export-count":
    "Découpage par IA illimité : ne payez que les clips que vous exportez.",
  "credit-count":
    "Découpage par IA illimité : ne payez que les minutes que vous importez.",
  "video-count":
    "Découpage par IA illimité : ne payez que les vidéos que vous importez.",
};

// The page's questions, in the order of the English FAQ.
export const pricingFaq = (plans: PublicPlan[]) => {
  const prices = (pick: (plan: PublicPlan) => number) =>
    orList(plans.map((plan) => usd(pick(plan))));
  return [
    {
      question: "Combien coûte Klap ?",
      answer: `En facturation mensuelle, Klap coûte ${prices((plan) => plan.monthlyPrice)} par mois. En facturation annuelle, il coûte ${prices((plan) => plan.yearlyPrice)} par an, soit ${prices((plan) => plan.yearlyMonthlyPrice)} par mois. ${plans.map((plan) => `${plan.name} inclut ${count(plan.clipsPerMonth)} clips par mois`).join(", ")}. Tous les prix sont en dollars américains et ont été mis à jour pour la dernière fois le ${UPDATED_ON}.`,
    },
    {
      question: "Que comprend chaque forfait Klap ?",
      answer: `Chaque forfait inclut le découpage par IA, avec un score de viralité, un recadrage sur la personne qui parle et des sous-titres animés sur chaque clip. Vous bénéficiez aussi de 99+ langues, de la programmation et de la publication sur TikTok, YouTube, Instagram, LinkedIn et Facebook, de comptes sociaux illimités, des statistiques de vos publications et d'un éditeur avec b-roll et titres d'accroche. Les vidéos sources peuvent durer jusqu'à ${MAX_HOURS} heures, et les exports ne portent aucun filigrane.`,
    },
    {
      question: IS_KLAP_FREE_FAQ.question,
      answer: isKlapFreeAnswer(plans),
    },
    {
      question: "Qu'est-ce qui compte comme un clip ?",
      answer: `Chaque short généré par Klap compte pour un clip, et vos clips se renouvellent à chaque période de facturation. La durée de votre vidéo source n'entame pas votre forfait, et une vidéo peut durer jusqu'à ${MAX_HOURS} heures. Les forfaits annuels reçoivent tous les clips de l'année d'un coup : ${orList(plans.map((plan) => count(plan.clipsPerMonth * 12)))}.`,
    },
    {
      question: "Klap ajoute-t-il un filigrane ?",
      answer: WATERMARK_ANSWER,
    },
    {
      question: "Quelle est la politique de remboursement de Klap ?",
      answer: (
        <>
          Sur un forfait mensuel, vous pouvez demander un remboursement à tout
          moment pendant votre premier mois, et pour vos 3 derniers mois
          consécutifs au maximum si vous n’avez pas utilisé Klap. Sur un forfait
          annuel, vous obtenez un remboursement intégral pendant votre premier
          mois. Ensuite, vous récupérez 1/12 du prix annuel pour chaque mois
          complet où vous n’avez pas utilisé Klap, dans la limite de vos 3
          derniers mois consécutifs. Résiliez d’abord, puis demandez votre
          remboursement vous-même depuis votre page de facturation, ou contactez
          le support. Les conditions complètes figurent à la{" "}
          <Link className={link} href="/terms-of-services#refund-policy">
            section 10 de nos conditions
          </Link>{" "}
          (en anglais).
        </>
      ),
    },
    {
      question: "Puis-je résilier à tout moment ?",
      answer:
        "Oui. Résiliez depuis votre page de facturation quand vous le souhaitez : votre forfait reste actif jusqu'à la fin de la période payée.",
    },
    {
      question: "Puis-je changer de forfait ou de période de facturation ?",
      answer:
        "Oui, depuis votre page de facturation. Le passage à la facturation annuelle s'applique immédiatement, et le passage de l'annuel au mensuel s'applique à la fin de votre année en cours.",
    },
    {
      question: "Existe-t-il une réduction ou un code promo Klap ?",
      answer: `La facturation annuelle est la réduction officielle : ${DISCOUNT_PERCENT} % de moins que le prix mensuel, sans code. Klap ne publie pas de codes promo. Si un créateur que vous suivez partage son propre code, saisissez-le au moment du paiement sur un forfait mensuel.`,
    },
    {
      question: "Combien coûte l'API Klap ?",
      answer: (
        <>
          L’
          <Link className={link} href="/api">
            API Klap
          </Link>{" "}
          est en libre-service et facturée séparément de ces forfaits :{" "}
          {API_PRICE_SUMMARY}.
        </>
      ),
    },
  ];
};

// Said here, with no English version of it: French buyers ask what the English
// page has no reason to explain.
const FRENCH_ONLY_FAQ = [
  {
    question: "Les prix sont-ils en euros ? Et la TVA ?",
    answer:
      "Les prix sont affichés en dollars américains. Les taxes applicables sont calculées au moment du paiement, et le montant final apparaît avant que vous validiez. Les professionnels peuvent renseigner leur numéro de TVA pendant le paiement.",
  },
  APP_IN_ENGLISH_FAQ,
];

export const pricingMeta = typoDeep({
  title: "Tarifs Klap 2026 : forfaits, essai gratuit et remboursement",
  description: `Prix officiels de Klap : ${orList(PUBLIC_PLANS.map((plan) => usd(plan.monthlyPrice)))} par mois, ou ${orList(PUBLIC_PLANS.map((plan) => usd(plan.yearlyPrice)))} par an, pour ${orList(PUBLIC_PLANS.map((plan) => count(plan.clipsPerMonth)))} clips/mois. Essai gratuit, sans carte bancaire.`,
});

export const pricing: PricingCopy = {
  title: "Tarifs de Klap",
  subtitle: (variant) => typo(SUBTITLES[variant]),
  tryForFree: "Essayez gratuitement. Annulez à tout moment.",
  faq: (plans) => typoDeep([...pricingFaq(plans), ...FRENCH_ONLY_FAQ]),
};
